import React from 'react';
import {
	CTAScreen,
	CustomHero,
	ImageTextSection,
	Reviews,
} from '@/components/landing/reusableSections';
import { useTranslationContext } from '@/context/translation';
import {
	useLandingShownEvent,
	useSendToAmplitudeScrolledPercent,
	useTranslationWithLocale,
} from '@/hooks';
import { GenerateLandingLinkService, GoogleService } from '@/services';
import {
	benefitsEnhancement,
	howItWorkEnhancement,
	reviewsLanding,
	whyUseEnhancement,
} from '@/data';
import {
	AMPLITUDE_EVENTS,
	GOOGLE_EVENTS,
	LANDING_AMPLITUDE_EVENTS,
	LOCAL_STORAGE_KEYS,
} from '@/constants';
import benefit from '@/public/images/image-enhancement/benefits.webp';
import imgHero from '@/public/images/image-enhancement/heroimg.webp';
import howItWork from '@/public/images/image-enhancement/howItWorks.webp';
import whyUse from '@/public/images/image-enhancement/whyUse.webp';
import ctaScreen from '@/public/images/landing/ctaScreen.webp';
import styles from './ImageEnhancementLanding.module.scss';

interface Props {}
const ImageEnhancementLanding: React.FC<Props> = () => {
	const handleImageEnhancementClick = () => {
		GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
		window.location.href =
			GenerateLandingLinkService.funnelImageEnhancementV2();
	};

	// Translations
	const { locale, translations } = useTranslationContext();
	const { t } = useTranslationWithLocale(
		'image-enhancement-v2',
		locale,
		translations?.['image-enhancement-v2'],
	);
	const { t: tReviews } = useTranslationWithLocale(
		'landing-reviews',
		locale,
		translations?.['landing-reviews'],
	);

	// Analytics
	useSendToAmplitudeScrolledPercent(
		[50, 75, 100],
		LOCAL_STORAGE_KEYS.IMAGE_ENHANCE_SCROLLED_POINTS,
		AMPLITUDE_EVENTS.WEBAPP_LANDING_SCROLLED,
		{ type: LANDING_AMPLITUDE_EVENTS.IMAGE_ENHANCEMENT_V2 },
	);
	useLandingShownEvent({ type: LANDING_AMPLITUDE_EVENTS.IMAGE_ENHANCEMENT_V2 });

	return (
		<>
			<CustomHero
				image={imgHero}
				title={t('heroData.title')}
				subtitle={t('heroData.subtitle')}
				onClick={handleImageEnhancementClick}
				buttonText={t('getStarted')}
			/>

			{/* HowItWork */}
			<ImageTextSection
				title={t('howItWorkData.mainTitle')}
				image={howItWork}
				data={howItWorkEnhancement}
				t={t}
				buttonText={t('getStarted')}
				onClick={handleImageEnhancementClick}
			/>

			<Reviews t={tReviews} data={reviewsLanding} />

			{/* Benefit */}
			<ImageTextSection
				title={t('benefitData.mainTitle')}
				image={benefit}
				specialListItem
				data={benefitsEnhancement}
				t={t}
				buttonText={t('getStarted')}
				onClick={handleImageEnhancementClick}
			/>

			{/* WhyUse */}
			<ImageTextSection
				title={t('whyUseData.mainTitle')}
				image={whyUse}
				data={whyUseEnhancement}
				t={t}
				classes={{
					imageContainer:
						styles['imageEnhancementLanding__whyUse-image-container'],
					list: styles['imageEnhancementLanding__whyUse-list'],
				}}
				buttonText={t('getStarted')}
				onClick={handleImageEnhancementClick}
			/>

			{/* TryLift */}
			<CTAScreen
				title1={t('tryLiftData.title1')}
				title_green={t('tryLiftData.greenText')}
				title2={t('tryLiftData.title2')}
				subtitle={t('tryLiftData.subtitle')}
				image={ctaScreen}
				altText={t('tryLiftData.altText')}
				buttonText={t('getStarted')}
				onClick={handleImageEnhancementClick}
			/>
		</>
	);
};

export default ImageEnhancementLanding;
